import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import axios from "axios";
import { API } from "@/App";
import { SEO } from "@/components/SEO";
import { toast } from "sonner";

const contactInfo = [
  {
    icon: MapPin,
    title: "Visit Us",
    details: ["Landmark Residency, Madeenaguda, Miyapur", "Hyderabad-500049 Telangana"],
    action: null
  },
  {
    icon: Phone,
    title: "Call Us",
    details: ["+91\u00a0 96403 00086"],
    action: "tel:+919640300086"
  },
  {
    icon: Mail,
    title: "Email Us",
    details: ["info@prarohagreens.com"],
    action: "mailto:info@prarohagreens.com"
  },
  {
    icon: Clock,
    title: "Working Hours",
    details: ["Mon - Sat: 9AM - 6PM", "Sunday: Closed"],
    action: null
  }
];

export default function ContactPage() {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent! We'll get back to you soon.");
      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-16 md:pt-20 lg:pt-24" data-testid="contact-page">
      <SEO title="Contact Us" description="Get in touch with Praroha Greens. Visit us at Hafeezpet, Hyderabad or reach out via phone and email." keywords="contact praroha greens, microgreens delivery hyderabad" path="/contact" />
      {/* Hero */}
      <section className="py-8 md:py-10 bg-praroha-forest">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
            <span className="inline-block px-4 py-1.5 bg-white/10 text-white/90 text-sm font-medium rounded-full mb-3">
              Get In Touch
            </span>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-2">
              Contact Us
            </h1>
            <p className="text-white/50 text-sm">
              Have questions about our microgreens or want to place a bulk order? We'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-8 md:py-10 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full bg-white hover:shadow-soft transition-all duration-300 border-border/50">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-praroha-forest/10 flex items-center justify-center mb-4">
                      <info.icon className="w-7 h-7 text-praroha-forest" />
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-praroha-forest mb-2">
                      {info.title}
                    </h3>
                    {info.details.map((detail, i) => (
                      info.action ? (
                        <a
                          key={i}
                          href={info.action}
                          className="block text-muted-foreground text-sm hover:text-praroha-forest transition-colors"
                        >
                          {detail}
                        </a>
                      ) : (
                        <p key={i} className="text-muted-foreground text-sm">{detail}</p>
                      )
                    ))}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-8 md:py-10 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="bg-white shadow-soft border-border/50">
                <CardContent className="p-6 md:p-8">
                  <h2 className="font-heading text-2xl font-semibold text-praroha-forest mb-6">
                    Send Us a Message
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Your name"
                          required
                          data-testid="contact-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="you@example.com"
                          required
                          data-testid="contact-email-input"
                        />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+91 98765 43210"
                          data-testid="contact-phone-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="subject">Subject *</Label>
                        <Input
                          id="subject"
                          value={formData.subject}
                          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                          placeholder="How can we help?"
                          required
                          data-testid="contact-subject-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Tell us more about your inquiry..."
                        rows={5}
                        required
                        data-testid="contact-message-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-praroha-forest hover:bg-praroha-forest/90 text-white rounded-full py-6 text-lg"
                      data-testid="contact-submit-btn"
                    >
                      {submitting ? "Sending..." : (
                        <>
                          <Send className="mr-2 w-5 h-5" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>

            {/* Map */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="h-full min-h-[400px]"
            >
              <div className="h-full rounded-2xl overflow-hidden shadow-soft" data-testid="contact-map">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3805.7!2d78.3539!3d17.4875!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb91f3e0000001%3A0x1!2sLandmark%20Residency%2C%20Hafeezpet%2C%20Madeenaguda%2C%20Hyderabad%2C%20Telangana%20500049!5e0!3m2!1sen!2sin!4v1699999999999!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: '400px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Praroha Greens Location"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-12 bg-praroha-forest text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-4">
            Order Fresh Microgreens Today
          </h2>
          <p className="text-white/80 mb-8">
            Experience the freshest, most nutritious microgreens delivered to your door.
          </p>
          <Button className="bg-white text-praroha-sage hover:bg-white/90 rounded-full px-8 py-6 text-lg" data-testid="order-now-cta">
            Shop Now
          </Button>
        </div>
      </section>
    </div>
  );
}
